import { withAlpha } from "@/lib/color";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";

/**
 * AlboEmbed — small read-only list of past winners for a specific set of
 * competition_keys, to be embedded inside a single Coppa or Campionato page.
 *
 * Props:
 * - competitionKeys: string[]  (e.g. ["coppa_italia"] or ["league_top","league_serie_a"])
 * - accent: string             (border/text accent color)
 * - emptyMessage?: string
 */
export default function AlboEmbed({ competitionKeys = [], accent = "rgb(var(--tc-ffd700))", emptyMessage }) {
    const [entries, setEntries] = useState([]);
    const [comps, setComps] = useState({});
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const [entriesRes, compsRes] = await Promise.all([
                    Promise.all(
                        competitionKeys.map((k) =>
                            api.get(`/albo/entries`, { params: { competition: k } })
                        )
                    ),
                    api.get(`/albo/competitions`),
                ]);
                if (!alive) return;
                const all = entriesRes.flatMap((r) => r.data || []);
                all.sort((a, b) => b.season - a.season);
                setEntries(all);
                const cmap = {};
                for (const c of compsRes.data || []) cmap[c.key] = c;
                setComps(cmap);
            } catch (e) {
                console.error(e);
            } finally {
                if (alive) setLoading(false);
            }
        })();
        return () => { alive = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(competitionKeys)]);

    if (loading) {
        return <div className="op-label text-t8f95a3 animate-pulse mt-6">// CARICAMENTO ALBO…</div>;
    }

    if (entries.length === 0) {
        return (
            <div className="op-card p-8 mt-6 text-center" data-testid="albo-embed-empty">
                <div className="text-4xl mb-3">🏆</div>
                <div className="op-label text-t8f95a3">
                    {emptyMessage || "Nessun vincitore ancora registrato per questa competizione."}
                </div>
            </div>
        );
    }

    return (
        <div className="mt-6" data-testid="albo-embed">
            <div
                className="op-card overflow-hidden"
                style={{ borderTop: `3px solid ${accent}` }}
            >
                <div className="px-5 py-4 border-b border-t22252a bg-t0e1014 flex items-center gap-3">
                    <span className="text-2xl" style={{ filter: `drop-shadow(0 0 6px ${withAlpha(accent, "66")})` }}>🏆</span>
                    <div>
                        <div className="op-label text-[10px]" style={{ color: accent }}>
                            ALBO D'ORO
                        </div>
                        <div className="text-tffffff text-sm">{entries.length} edizioni vinte</div>
                    </div>
                </div>
                <ul className="divide-y divide-t16181c">
                    {entries.map((e) => {
                        const compLabel = comps[e.competition_key]?.label || e.competition_key;
                        const compColor = comps[e.competition_key]?.color || accent;
                        return (
                            <li
                                key={e.id}
                                className="px-5 py-3 flex items-center gap-4 hover:bg-t16181c/60 transition-colors"
                                data-testid={`albo-embed-row-${e.season}-${e.competition_key}`}
                            >
                                <div className="op-mono text-xs text-t2bb6ff font-bold w-12">
                                    S{e.season}
                                </div>
                                <div className="flex-1 text-tffffff text-sm uppercase tracking-wide font-semibold">
                                    {e.winner_display || e.winner}
                                </div>
                                {competitionKeys.length > 1 && (
                                    <div
                                        className="op-label text-[9px] px-2 py-0.5 border whitespace-nowrap"
                                        style={{ color: compColor, borderColor: `${withAlpha(compColor, "55")}` }}
                                    >
                                        {compLabel}
                                    </div>
                                )}
                            </li>
                        );
                    })}
                </ul>
            </div>
        </div>
    );
}
